'use client';

import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useCompetitionContext } from "../providers/CompetitionContext";
import { getCompetitionCopy } from "../lib/competitionCopy";

type RaceCategory = "Monument" | "World Tour" | "Hors World Tour";

const races: { name: string; date: string; category: RaceCategory }[] = [
  { name: "Omloop Het Nieuwsblad", date: "28 février", category: "World Tour" },
  { name: "Kuurne-Bruxelles-Kuurne", date: "1 mars", category: "Hors World Tour" },
  { name: "Le Samyn", date: "3 mars", category: "Hors World Tour" },
  { name: "Trofeo Laigueglia", date: "4 mars", category: "Hors World Tour" },
  { name: "Strade Bianche", date: "7 mars", category: "World Tour" },
  { name: "Nokere Koerse", date: "18 mars", category: "Hors World Tour" },
  { name: "Grand Prix de Denain", date: "19 mars", category: "Hors World Tour" },
  { name: "Bredene Koksijde Classic", date: "20 mars", category: "Hors World Tour" },
  { name: "Milan-San Remo", date: "21 mars", category: "Monument" },
  { name: "GP Monseré", date: "22 mars", category: "Hors World Tour" },
  { name: "Classic Brugge-De Panne", date: "25 mars", category: "World Tour" },
  { name: "E3 Saxo Classic", date: "27 mars", category: "World Tour" },
  { name: "In Flanders Fields", date: "29 mars", category: "World Tour" },
  { name: "À travers la Flandre", date: "1 avril", category: "World Tour" },
  { name: "Grand Prix Miguel Indurain", date: "4 avril", category: "Hors World Tour" },
  { name: "Tour des Flandres", date: "5 avril", category: "Monument" },
  { name: "Grand Prix de l'Escaut", date: "9 avril", category: "Hors World Tour" },
  { name: "Paris-Roubaix", date: "12 avril", category: "Monument" },
  { name: "Flèche Brabançonne", date: "17 avril", category: "Hors World Tour" },
  { name: "Amstel Gold Race", date: "19 avril", category: "World Tour" },
  { name: "Flèche Wallonne", date: "22 avril", category: "World Tour" },
  { name: "Liège-Bastogne-Liège", date: "26 avril", category: "Monument" },
  { name: "Eschborn-Frankfurt", date: "1 mai", category: "World Tour" },
  { name: "Famenne Ardenne Classic", date: "3 mai", category: "Hors World Tour" },
  { name: "Tro Bro Léon", date: "10 mai", category: "Hors World Tour" },
];

const pointsRows = [
  { place: "1", monument: 100, worldTour: 80, horsWorldTour: 60 },
  { place: "2", monument: 80, worldTour: 64, horsWorldTour: 48 },
  { place: "3", monument: 65, worldTour: 52, horsWorldTour: 39 },
  { place: "4", monument: 55, worldTour: 44, horsWorldTour: 33 },
  { place: "5", monument: 45, worldTour: 36, horsWorldTour: 27 },
  { place: "6", monument: 35, worldTour: 28, horsWorldTour: 21 },
  { place: "7", monument: 30, worldTour: 24, horsWorldTour: 18 },
  { place: "8", monument: 25, worldTour: 20, horsWorldTour: 15 },
  { place: "9", monument: 20, worldTour: 16, horsWorldTour: 12 },
  { place: "10", monument: 17, worldTour: 14, horsWorldTour: 10 },
  { place: "11", monument: 14, worldTour: 11, horsWorldTour: 8 },
  { place: "12", monument: 12, worldTour: 10, horsWorldTour: 7 },
  { place: "13", monument: 10, worldTour: 8, horsWorldTour: 6 },
  { place: "14", monument: 9, worldTour: 7, horsWorldTour: 5 },
  { place: "15", monument: 8, worldTour: 6, horsWorldTour: 4 },
  { place: "16", monument: 7, worldTour: 5, horsWorldTour: 3 },
  { place: "17", monument: 6, worldTour: 4, horsWorldTour: 3 },
  { place: "18", monument: 5, worldTour: 3, horsWorldTour: 2 },
  { place: "19", monument: 4, worldTour: 3, horsWorldTour: 2 },
  { place: "20", monument: 3, worldTour: 2, horsWorldTour: 2 },
  { place: "21 - ...", monument: 2, worldTour: 1, horsWorldTour: 1 },
];

function getCategoryPill(category: RaceCategory): string {
  switch (category) {
    case "Monument":
      return "bg-[#ffebee] text-[#c62828]";
    case "World Tour":
      return "bg-[#e3f2fd] text-[#1976d2]";
    case "Hors World Tour":
      return "bg-[#f3e5f5] text-[#7b1fa2]";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function RulesPage() {
  const { competitionConfig } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);

  return (
    <div className="min-h-[calc(100vh-100px)] bg-[var(--rtbf-dark,#001425)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-3xl">
        <section className="space-y-8 py-1 text-white sm:py-0">
          <div className="space-y-6">
            <div>
              <h1 className="mb-2 text-xl font-bold text-white">Règlement {copy.gameName}</h1>
              <p className="text-md text-white/90">
                Bienvenue dans le jeu de fantasy cyclisme: &quot;On connaît nos classiques, le jeu&quot; !
              </p>
            </div>

            <div className="space-y-4 text-white/90">
              <h3 className="mb-2 text-xl font-semibold text-white">Sélectionnez 20 coureurs</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Restez dans le budget de 200 millions d&apos;euros.</li>
                <li>Choisissez maximum 4 coureurs de la même équipe.</li>
              </ul>

              <h3 className="mb-2 text-xl font-semibold text-white">Courses et délais</h3>
              <p>
                Vous trouverez ci-dessous un aperçu de toutes les courses. Les délais exacts sont indiqués dans le calendrier sur la page Équipe. Les modifications apportées à votre équipe doivent être effectuées avant chaque délai, faute de quoi elles ne seront prises en compte qu&apos;à partir de la course suivante.
              </p>

              <div className="overflow-hidden rounded-lg bg-white/95 p-4 text-[var(--rtbf-blue-dark,#003E63)]">
                <ol className="space-y-2">
                  {races.map((race) => (
                    <li key={`${race.name}-${race.date}`} className="rounded-md border-l-4 border-[#2c5aa0] bg-[#f8f9fa] px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-[#2c5aa0]">
                          {race.name} - {race.date}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getCategoryPill(race.category)}`}>
                          {race.category}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <h3 className="mb-2 text-xl font-semibold text-white">Gagner des points</h3>
              <p>
                Pour chaque course, vous composez une équipe de 10 coureurs, tandis que les 10 autres restent dans le bus. Les 10 coureurs sélectionnés marquent des points en fonction de leur résultat dans la course. Les coureurs restés dans le bus ne marquent aucun point.
              </p>
              <p>
                Les 20 premiers coureurs de chaque course marquent le plus de points. À partir de la 21e place, chaque coureur qui termine la course marque 2 ou 1 point. Les performances lors des quatre Monuments (Milan-San Remo, Tour des Flandres, Paris-Roubaix et Liège-Bastogne-Liège) ont un poids plus important que celles réalisées lors des courses World Tour ou hors World Tour.
              </p>

              <div className="overflow-x-auto rounded-lg bg-white/95 p-3 text-[var(--rtbf-blue-dark,#003E63)]">
                <table className="min-w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr className="bg-[var(--rtbf-blue,#0076A9)] text-white">
                      <th className="px-3 py-2 text-center font-semibold">Place</th>
                      <th className="px-3 py-2 text-center font-semibold">Monument</th>
                      <th className="px-3 py-2 text-center font-semibold">World Tour</th>
                      <th className="px-3 py-2 text-center font-semibold">Hors World Tour</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pointsRows.map((row, index) => (
                      <tr key={row.place} className={index % 2 === 0 ? "bg-[var(--rtbf-blue-dark,#003E63)] text-white" : "bg-[#001324] text-white"}>
                        <td className="px-3 py-2 text-center">{row.place}</td>
                        <td className="px-3 py-2 text-center">{row.monument}</td>
                        <td className="px-3 py-2 text-center">{row.worldTour}</td>
                        <td className="px-3 py-2 text-center">{row.horsWorldTour}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="mb-2 text-xl font-semibold text-white">Transferts</h3>
              <p>
                Vous disposez de 3 transferts pour adapter votre équipe pendant OCNC. Vous choisissez librement quand effectuer un transfert et quel coureur remplacer.
              </p>
              <p>Attention : les coureurs entrants coûtent 10 % de plus qu&apos;au début du jeu !</p>
              <p>Les points déjà marqués par le coureur sortant pour votre équipe restent bien entendu acquis.</p>
              <p>
                Lors d&apos;un transfert, vous récupérez toujours la valeur d&apos;origine du coureur sortant dans votre budget, tandis que vous payez la valeur de transfert du coureur entrant.
              </p>

              <h3 className="mb-2 text-xl font-semibold text-white">Classements</h3>
              <p>Le classement de chaque course est établi après l&apos;arrivée.</p>
              <p>Le classement général final est déterminé par l&apos;addition des points marqués lors de toutes les courses.</p>
              <p>
                Nous suivons toujours les décisions du jury de course en ce qui concerne les classements, les disqualifications et tout autre incident.
              </p>

              <h3 className="mb-2 text-xl font-semibold text-white">Général</h3>
              <p>
                RTBF se réserve le droit de refuser des utilisateurs et/ou des équipes si ceux-ci ne respectent pas les règles du jeu ou vont à l&apos;encontre de l&apos;esprit du jeu. En cas d&apos;abus, de tromperie ou de fraude (participation via un script, personnes fictives, adresses e-mail fictives, exploitation d&apos;un bug, etc.), RTBF peut supprimer un utilisateur et/ou ses équipes et lui retirer le droit à toute récompense.
              </p>
            </div>

            <div className="pt-4 text-center">
              <Button asChild className="rounded-full bg-[var(--rtbf-orange,#fdc300)] text-[var(--rtbf-blue-dark,#003E63)] hover:bg-[#e6b200]">
                <Link href="/">Retour à l&apos;accueil</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
